import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { QueryKey, UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  PaginationState,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

export interface IQueryTableOptions<
  TData,
  TQueryResponse extends { data: TData[]; total: number } = {
    data: TData[];
    total: number;
  },
  TQueryKey extends QueryKey = QueryKey,
> {
  columns: ColumnDef<TData>[];
  getQueryOptions: (
    args: { page: number; limit: number } & Record<string, unknown>,
  ) => UseQueryOptions<TQueryResponse, Error, TQueryResponse, TQueryKey>;

  /**
   * Prefixo para as chaves de URL (`page`, `limit`, `filters`, `sorting`).
   * Obrigatório quando mais de uma tabela coexiste na mesma rota para evitar
   * conflitos de estado na URL.
   *
   * @example stateKey="documents" → ?documents_page=2&documents_filters=...
   */
  stateKey?: string;

  initialState?: {
    pagination?: PaginationState;
    filters?: ColumnFiltersState;
    sorting?: SortingState;
  };
}

type RawSearch = Record<string, unknown>;

const parsePaginationFromSearch = (
  search: RawSearch,
  keys: { page: string; limit: string },
  paginationInitialState?: PaginationState,
) => {
  const rawPage = Number(search[keys.page]);
  const rawLimit = Number(search[keys.limit]);

  const pageIndex =
    Number.isSafeInteger(rawPage) && rawPage > 0
      ? rawPage - 1
      : (paginationInitialState?.pageIndex ?? 0);

  const pageSize =
    Number.isSafeInteger(rawLimit) && rawLimit > 0
      ? rawLimit
      : (paginationInitialState?.pageSize ?? 10);

  return { pageIndex, pageSize };
};

export const useQueryTable = <
  TData,
  TQueryResponse extends { data: TData[]; total: number } = {
    data: TData[];
    total: number;
  },
  TQueryKey extends QueryKey = QueryKey,
>({
  columns,
  getQueryOptions,
  stateKey,
  initialState: {
    pagination: paginationInitialState,
    filters: filtersInitialState,
    sorting: sortingInitialState,
  } = {},
}: IQueryTableOptions<TData, TQueryResponse, TQueryKey>) => {
  const keys = useMemo(() => {
    const prefix = stateKey ? `${stateKey}_` : '';

    return {
      page: `${prefix}page`,
      limit: `${prefix}limit`,
      filters: `${prefix}filters`,
      sorting: `${prefix}sorting`,
    };
  }, [stateKey]);

  const navigate = useNavigate();
  const search = useSearch({ from: '/_authenticated' }) as RawSearch;

  const columnFilters = useMemo(
    () =>
      (search[keys.filters] as ColumnFiltersState | undefined) ??
      filtersInitialState ??
      [],
    [search, keys.filters, filtersInitialState],
  );

  const pagination = useMemo(
    () => parsePaginationFromSearch(search, keys, paginationInitialState),
    [search, keys, paginationInitialState],
  );

  const sorting = useMemo(
    () =>
      (search[keys.sorting] as SortingState | undefined) ??
      sortingInitialState ??
      [],
    [search, keys.sorting, sortingInitialState],
  );

  const handlePaginationChange = useCallback<OnChangeFn<PaginationState>>(
    (value) => {
      navigate({
        search: (prev) => {
          const { pageIndex, pageSize } =
            typeof value === 'function'
              ? value(parsePaginationFromSearch(prev as RawSearch, keys))
              : value;

          return {
            ...prev,
            [keys.page]: pageIndex + 1,
            [keys.limit]: pageSize,
          } as never;
        },
        replace: true,
      });
    },
    [navigate, keys],
  );

  const handleFiltersChange = useCallback<OnChangeFn<ColumnFiltersState>>(
    (value) => {
      navigate({
        search: (prev) => {
          const newFilters =
            typeof value === 'function' ? value(columnFilters) : value;

          return {
            ...prev,
            // Volta para a primeira página ao trocar filtros
            [keys.page]: 1,
            [keys.filters]: newFilters,
          } as never;
        },
        replace: true,
      });
    },
    [navigate, columnFilters, keys.filters, keys.page],
  );

  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (value) => {
      navigate({
        search: (prev) => {
          const newSorting =
            typeof value === 'function' ? value(sorting) : value;

          return {
            ...prev,
            [keys.sorting]: newSorting,
          } as never;
        },
        replace: true,
      });
    },
    [navigate, sorting, keys.sorting],
  );

  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const query = useQuery(
    getQueryOptions({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      ...Object.fromEntries(columnFilters.map(({ id, value }) => [id, value])),
    }),
  );

  const table = useReactTable({
    data: query.data?.data ?? [],
    columns,
    rowCount: query.data?.total,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    onPaginationChange: handlePaginationChange,
    state: {
      pagination,
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleFiltersChange,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  return { table, query };
};
